// @ts-check
/// <reference path="../../types/minisearch.d.ts" />

/**
 * A single brand list item for search
 * @typedef {Object} BrandItem
 * @property {string} name - The name of the brand
 * @property {string} [subheading] - A short label describing what the brand is about
 * @property {string} id - The list item key, used for document.getElementById
 */

/**
 * The letter filter
 * @typedef {Object} LetterFilter
 * @property {string | null} filter - The filter string
 * @property {boolean} isRegexStr - Whether the filter is a regex string
 */

/**
 * The search form and its elements
 * @typedef {Object} SearchForm
 * @property {HTMLFormElement} form - The search form
 * @property {HTMLInputElement} input - The search form text input
 * @property {HTMLButtonElement} submit - The search form submit button
 * @property {LetterFilter} letterFilter - The letter filter object
 */

/**
 * @typedef {Pick<Lib_MiniSearch.SearchResult, 'id' | 'name'>
 *  | Pick<BrandItem, 'id' | 'name'>} MixedSearchResult
 */

/**
 * @module BrandsSearch
 */
class BrandsSearch extends HTMLElement {

  /** @type {SearchForm} */
  searchForm;

  /** @type {Lib_MiniSearch.default} */
  miniSearch;

  /** @type {BrandItem[]} */
  visibleBrands;

  /** @type {BrandItem[]} */
  allBrands;

  /** @type {HTMLInputElement | undefined} */
  selectedFirstLetter;

  static get observedAttributes() {
    return ['searching']
  }

  connectedCallback() {

    const brands = this.getAllBrandItems();
    if (!brands.length) return;
    this.allBrands = brands;

    const form = this.getSearchForm();
    if (!form) return;

    const miniSearch = this.initMiniSearch(brands);
    if (!miniSearch) return;

    this.searchForm = form;
    this.miniSearch = miniSearch;

    /** @type {HTMLLIElement[]} */
    this.visibleBrands = [];

    this.addSearchFormEvents();
    this.initLetterFilters();
  }

  disconnectedCallback() {
    this.visibleBrands = null;
    this.searchForm = null;
    this.miniSearch = null;
    this.allBrands = null;
  }

  /**
   * Returns the brand data for searching
   * @returns {BrandItem[]}
   */
  getAllBrandItems() {

    /** @type {NodeListOf<HTMLLIElement>} */
    const items = this.querySelectorAll('[data-search-brand-item]');

    if (!items.length) {
      console.warn('Brands search found no brand items to init');
      return [];
    }

    /** @type {BrandItem[]} */
    const brandData = [];

    for (const brandItemEl of items) {

      const elName = brandItemEl.dataset.attrName
      const elId = brandItemEl.id;
      const elSubheading = brandItemEl.dataset.attrSubheading;

      /** @type {BrandItem} */
      const brandItem = {
        name: elName,
        id: elId,
        ...(elSubheading && { subheading: elSubheading }),
      }
      brandData.push(brandItem);
    }

    return brandData;
  }

  /**
   * @returns {SearchForm | undefined}
   */
  getSearchForm() {

    /** @type {HTMLFormElement | null} */
    const form = this.querySelector('[data-brands-search-form]');
    if (!form) return;

    /** @type {HTMLInputElement | null} */
    const input = this.querySelector('[data-brands-search-text]');
    if (!input) return

    /** @type {HTMLButtonElement | null} */
    const submit = this.querySelector('[data-brands-search-submit]');
    if (!submit) return;

    /** @type {SearchForm} */
    const searchForm = {
      form: document.getElementById(form.id),
      input: document.getElementById(input.id),
      submit: document.getElementById(submit.id),
      letterFilter: {
        filter: null,
        isRegexStr: false,
      }
    };

    return searchForm;
  }

  /**
   * Inits the letter filter event listeners
   * @returns {void}
   */
  initLetterFilters() {

    /** @type {NodeListOf<HTMLInputElement> | null} */
    const letterFilters = this.querySelectorAll('[data-letter-filter]');
    if (!letterFilters.length) return;

    for (const filter of letterFilters) {
      filter.addEventListener('change', this.handleLetterFilterChange.bind(this));
    }

    /** @type {HTMLButtonElement | null} */
    const letterFiltersClear = this.querySelector('[data-action-filter-letter-clear]');
    if (!letterFiltersClear) return;

    letterFiltersClear.addEventListener('click', this.handleLetterFilterClear.bind(this));
  }

  /**
   * Resets the letter filters
   * @param {Event} event
   * @returns {void}
   */
  handleLetterFilterClear(event) {
    if (this.selectedFirstLetter?.type === 'radio') {
      this.selectedFirstLetter.checked = false;
      this.searchForm.letterFilter.filter = null;
      this.searchForm.letterFilter.isRegexStr = false;
      this.selectedFirstLetter = undefined;
      this.searchToggleBrands();
    }
  }

  /**
   * Handles when the letter filters change
   * @param {Event} event
   * @returns {void}
   */
  handleLetterFilterChange(event) {
    this.searchForm.letterFilter.filter = event.target.value;
    if (event.target?.hasAttribute('data-attr-isregex')) {
      const isRegex = event.target.getAttribute('data-attr-isregex');
      this.searchForm.letterFilter.isRegexStr = (isRegex === 'true');
    }
    this.selectedFirstLetter = event.target;
    this.searchToggleBrands();
  }

  /**
   * Converts a regex string from the dom to regex
   * @param {string} regexStr
   * @returns {RegExp | undefined}
   */
  regexStrToRegex(regexStr) {

    /** @type {RegExp | undefined} */
    let regex;
    try {
      regex = new RegExp(regexStr);
    } catch(err) {
      console.warn(
        `Issue with Parsing Letter Filter Regex: ${regexStr}`,
        err
      )
    }
    return regex;
  }

  /**
   *
   * @param {BrandItem[]} documents
   * @returns {Lib_MiniSearch.default | undefined}
   */
  initMiniSearch(documents) {

    const MiniSearch = window.MiniSearch;
    if (!MiniSearch) return;

    /** @type {Lib_MiniSearch.default} */
    const miniSearch = new MiniSearch({
      fields: ['name', 'subheading'],
      idField: 'id',
      searchOptions: {
        fuzzy: 0.2,
        boost: { name: 2 },
        prefix: true,
        combineWith: 'AND',
      },
      storeFields: ['name', 'id'],
    });

    miniSearch.addAll(documents);
    return miniSearch;
  }

  /**
   * Adds event listeners to the form elements
   */
  addSearchFormEvents() {

    this.searchForm.form.addEventListener(
      'submit',
      this.handleSearchSubmit.bind(this)
    );

    this.searchForm.input.addEventListener(
      'input',
      this.handleSearchInput.bind(this)
    );
  }

  /**
   * Handles search submit event
   * @param {Event} event
   * @returns {void}
   */
  handleSearchSubmit(event) {
    event.preventDefault();
    this.searchToggleBrands();
  }

  /**
   * Handles search input event
   * @param {Event} event
   * @returns {void}
   */
  handleSearchInput(event) {
    this.searchToggleBrands();
  }

  /**
   * Searches all brands and toggles the visibility
   * of brands which match the query
   */
  searchToggleBrands() {

    this.resetVisibleBrands();

    if (!this.userIsSearching()) {
      this.setAttribute('searching', 'false');
      return;
    }

    const results = this.searchBrands();
    this.setAttribute('searching', 'true');

    for (const result of results) {
      const item = document.getElementById(result.id);
      if (!item) continue;
      this.setBrandVisible(item);
    }
  }

  /**
   * Checks if the user is searching
   * @returns {boolean}
   */
  userIsSearching() {
    const isInputFilled = (
      this.searchForm.input.value.length > 0
    );
    const isLetterFilterActive = (
      this.searchForm.letterFilter.filter !== null
    );
    return isInputFilled || isLetterFilterActive;
  }

  // TODO: Normalize the return into a single data type

  /**
   * Wrapper around minisearch which handles filtering
   * and empty queries. Assumes the user is actively
   * searching
   * @returns {MixedSearchResult[]}
   */
  searchBrands() {

    const inputActive = this.searchForm.input.value.length > 0;
    const filterActive = this.searchForm.letterFilter.filter !== null;

    if (!inputActive && filterActive) {
      const filterFunc = this.createFirstLetterFilterFunc();
      return this.allBrands.filter(filterFunc);
    }

    if (inputActive && !filterActive) {
      return this.miniSearch.search(this.searchForm.input.value);
    }

    if (inputActive && filterActive) {
      const filterFunc = this.createFirstLetterFilterFunc();
      const results = this.miniSearch.search(
        this.searchForm.input.value,
        { filter: filterFunc },
      );
      return results;
    }

    return this.allBrands;
  }

  /** Returns a filter callback function for filtering
   * brands by first letter
   * @returns {() | undefined}
   */
  createFirstLetterFilterFunc() {

    // TODO: Add typedef
    let filterFunc;

    if (this.searchForm.letterFilter.isRegexStr) {

      const regexFilter = this.regexStrToRegex(
        this.searchForm.letterFilter.filter
      );

      if (!regexFilter) return;

      filterFunc = (searchResult) => {
        return regexFilter.test(searchResult.name);
      }

    } else {

      // TODO: Redo to account for null. This is already handled
      // in other functions, but should also put a guard here.
      const searchLetterLower = (
        this.searchForm.letterFilter.filter?.toLowerCase()
      );

      filterFunc = (searchResult) => {
        const resLower = searchResult.name.toLowerCase();
        return resLower.startsWith(searchLetterLower);
      }
    }

    return filterFunc;
  }

  /**
   * Removes the forced visibility from visible brands
   */
  resetVisibleBrands() {
    for (const element of this.visibleBrands) {
      element.dataset.searchHit = 'false';
    }
    this.visibleBrands = [];
  }

  /**
   * Sets a single brand LI as visible
   * @param {HTMLLIElement} element
   */
  setBrandVisible(element) {
    element.dataset.searchHit = 'true';
    this.visibleBrands.push(element);
  }

}

customElements.define('brands-search', BrandsSearch);
